/**
 * Excel import validator — ImportRecord[] → ValidationResult.
 *
 * Four validation codes (per design §6.1):
 *   1. MISSING_REQUIRED  (error)  — required column blank/missing
 *   2. TYPE_MISMATCH     (error)  — value fails the column's primitive/zod check
 *   3. DUPLICATE_ID      (error)  — same id repeated within one sheet
 *   4. ID_NOT_FOUND      (warn)   — id not present in current graph
 *
 * Errors block apply; warnings exclude that record from apply but do not
 * block the rest. The validator is a pure function of (records, graph,
 * objectTypes) — no React, no JointJS imports.
 */

import type { ObjectType } from '../../../map-editor-shared/store/objectTypeStore'
import type {
  ExcelColumn,
  ExcelSheetSpec,
  ImportRecord,
  PropertyDiff,
  ValidatedRecord,
  ValidationCode,
  ValidationIssue,
  ValidationResult,
  ValidationStatus,
} from '../model/types'
import { buildAllSheetSpecs, indexSpecsBySheetName } from './columnSchema'
import type { GraphLike, ElementLike } from './exporter'

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatMessage(
  sheetName: string,
  rowNumber: number,
  field: string,
  message: string
): string {
  const fieldPart = field ? `[${field}]` : ''
  return `[${sheetName}][${rowNumber}]${fieldPart} ${message}`
}

function makeIssue(
  record: ImportRecord,
  field: string,
  code: ValidationCode,
  message: string
): ValidationIssue {
  return {
    rowNumber: record.rowNumber,
    sheetName: record.sheetName,
    field,
    code,
    message: formatMessage(record.sheetName, record.rowNumber, field, message),
  }
}

/** Empty if `value` is `undefined`, `null`, or an empty/whitespace-only string. */
function isEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true
  if (typeof value === 'string' && value.trim().length === 0) return true
  return false
}

/** Primitive type check matching ExcelColumnPrimitive. */
function checkPrimitive(value: unknown, primitive: ExcelColumn['primitive']): boolean {
  switch (primitive) {
    case 'string':
      return typeof value === 'string' || typeof value === 'number'
    case 'number':
      return typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)))
    case 'boolean':
      return (
        typeof value === 'boolean' ||
        (typeof value === 'string' &&
          ['true', 'false', '0', '1', 'yes', 'no'].includes(value.toLowerCase()))
      )
    case 'array':
      return Array.isArray(value) || typeof value === 'string'
  }
}

/** Coerce a primitive cell value into the expected runtime type (post-validation). */
function coerceValue(value: unknown, primitive: ExcelColumn['primitive']): unknown {
  if (value === undefined || value === null) return value
  switch (primitive) {
    case 'number':
      return typeof value === 'number' ? value : Number(value)
    case 'boolean':
      if (typeof value === 'boolean') return value
      if (typeof value === 'string') {
        const v = value.toLowerCase()
        return v === 'true' || v === '1' || v === 'yes'
      }
      return Boolean(value)
    case 'array':
      if (Array.isArray(value)) return value
      // Comma-separated strings → trimmed array
      return String(value)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    case 'string':
    default:
      return typeof value === 'string' ? value : String(value)
  }
}

/** Run zod refinement when the column has a zod field. Returns error message or null. */
function checkZod(value: unknown, column: ExcelColumn): string | null {
  if (!column.zodField) return null
  const result = column.zodField.safeParse(value)
  if (result.success) return null
  return result.error.issues[0]?.message ?? '값이 스키마에 맞지 않습니다.'
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-record validation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Validate a single record against its sheet spec and graph.
 * Sets `diff` only if status is `'valid'`.
 */
export function validateRecord(
  record: ImportRecord,
  spec: ExcelSheetSpec,
  graph: GraphLike,
  // duplicate-id is detected at the sheet level; this flag tells the per-record
  // validator that another row already claimed this id.
  isDuplicate: boolean
): ValidatedRecord {
  const issues: ValidationIssue[] = []

  // Index columns by key for lookup.
  const columnsByKey = new Map<string, ExcelColumn>()
  for (const col of spec.columns) {
    columnsByKey.set(col.key, col)
  }

  // 1) MISSING_REQUIRED + 2) TYPE_MISMATCH per column
  for (const col of spec.columns) {
    const raw = record.fields[col.header]
    if (isEmpty(raw)) {
      if (col.required) {
        issues.push(
          makeIssue(record, col.key, 'MISSING_REQUIRED', `필수 항목입니다.`)
        )
      }
      // Empty optional cell → no further check, no diff entry
      continue
    }
    if (!checkPrimitive(raw, col.primitive)) {
      issues.push(
        makeIssue(
          record,
          col.key,
          'TYPE_MISMATCH',
          `${col.primitive} 형식이 아닙니다 (실제: ${typeof raw}).`
        )
      )
      continue
    }
    const coerced = coerceValue(raw, col.primitive)
    const zodError = checkZod(coerced, col)
    if (zodError) {
      issues.push(makeIssue(record, col.key, 'TYPE_MISMATCH', zodError))
    }
  }

  // 3) DUPLICATE_ID
  if (isDuplicate) {
    issues.push(
      makeIssue(record, '', 'DUPLICATE_ID', `동일 시트에 같은 id가 중복되었습니다 ('${record.id}').`)
    )
  }

  // Determine record status from issues collected so far.
  // Errors take precedence over the unmatched warning.
  let status: ValidationStatus = 'valid'
  if (issues.some(i => i.code !== 'ID_NOT_FOUND')) {
    status = 'error'
  }

  // 4) ID_NOT_FOUND — only relevant if id present and not already in error.
  // We still emit it even when status === 'error' so the UI can show all info,
  // but status logic above prefers 'error' for blocking.
  if (status !== 'error' && record.id) {
    const cell = findElementById(graph, record.id)
    if (!cell) {
      issues.push(
        makeIssue(record, '', 'ID_NOT_FOUND', `현재 맵에 존재하지 않는 id입니다 ('${record.id}').`)
      )
      status = 'unmatched'
    }
  }

  // Build diff only for valid records.
  const diff = status === 'valid' ? buildDiff(record, spec, graph) : null

  return { record, status, issues, diff }
}

// ─────────────────────────────────────────────────────────────────────────────
// Diff construction
// ─────────────────────────────────────────────────────────────────────────────

function findElementById(graph: GraphLike, id: string): ElementLike | undefined {
  const elements = graph.getElements()
  for (const el of elements) {
    if (String(el.id) === id) return el
  }
  return undefined
}

/**
 * Compute the merged property change set for a record.
 * Empty cells in the Excel row are NOT included in `after` — they preserve the
 * existing value. `name` is kept on properties (mirrors PropertyEditor).
 */
export function buildDiff(
  record: ImportRecord,
  spec: ExcelSheetSpec,
  graph: GraphLike
): PropertyDiff | null {
  const cell = findElementById(graph, record.id)
  if (!cell) return null

  const data = cell.get('data') ?? {}
  const beforeProps = (data.properties ?? {}) as Record<string, unknown>
  const after: Record<string, unknown> = { ...beforeProps }
  const changedKeys: string[] = []
  let updatesName = false

  for (const col of spec.columns) {
    if (col.source === 'identity') continue // id is the matcher, never overwritten
    const raw = record.fields[col.header]
    if (isEmpty(raw)) continue

    const next = coerceValue(raw, col.primitive)
    // Where to store: 'name' goes to properties.name; properties go to properties[key].
    if (col.source === 'name') {
      if (beforeProps.name !== next) {
        after.name = next
        if (!changedKeys.includes('name')) changedKeys.push('name')
        updatesName = true
      }
    } else {
      // property
      if (beforeProps[col.key] !== next) {
        after[col.key] = next
        if (!changedKeys.includes(col.key)) changedKeys.push(col.key)
      }
    }
  }

  return {
    id: record.id,
    before: beforeProps,
    after,
    changedKeys,
    updatesName,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public entry — validate all records
// ─────────────────────────────────────────────────────────────────────────────

/** Detect duplicate ids per sheet (case-sensitive). */
function findDuplicateIds(records: ImportRecord[]): Set<string> {
  const seen = new Map<string, number>() // key: `${sheet}::${id}`, val: count
  for (const r of records) {
    if (!r.id) continue
    const key = `${r.sheetName}::${r.id}`
    seen.set(key, (seen.get(key) ?? 0) + 1)
  }
  const duplicates = new Set<string>()
  for (const [key, count] of seen.entries()) {
    if (count > 1) duplicates.add(key)
  }
  return duplicates
}

export function validateRecords(
  records: ImportRecord[],
  graph: GraphLike,
  objectTypes: ObjectType[]
): ValidationResult {
  const specs = buildAllSheetSpecs(objectTypes)
  const specsByName = indexSpecsBySheetName(specs)
  const duplicates = findDuplicateIds(records)

  const validated: ValidatedRecord[] = []

  for (const record of records) {
    const spec = specsByName.get(record.sheetName)
    if (!spec) {
      // Sheet without a spec is treated as an error so the UI can surface it.
      validated.push({
        record,
        status: 'error',
        issues: [
          makeIssue(
            record,
            '',
            'TYPE_MISMATCH',
            `알 수 없는 시트입니다 ('${record.sheetName}').`
          ),
        ],
        diff: null,
      })
      continue
    }
    const isDuplicate = duplicates.has(`${record.sheetName}::${record.id}`)
    validated.push(validateRecord(record, spec, graph, isDuplicate))
  }

  // Summary counts
  const summary = {
    total: validated.length,
    valid: validated.filter(v => v.status === 'valid').length,
    unmatched: validated.filter(v => v.status === 'unmatched').length,
    error: validated.filter(v => v.status === 'error').length,
  }

  return {
    records: validated,
    summary,
    hasBlockingErrors: summary.error > 0,
  }
}
